import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  HiOutlineAdjustmentsHorizontal,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClipboardDocumentCheck,
  HiOutlineShieldCheck,
} from "react-icons/hi2";
import {
  authService,
  safetyObservationService,
  plantService,
  masterDataService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import Pagination from "../components/Pagination";
import ObservationPreview from "./SafetyBehaviorObservation/schedule/ObservationPreview";
import SharedModal from "../components/common/SharedModal";
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import PageLoading from "../components/common/PageLoading";
import "./SafetyObservation.css";

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedSafetyObservationData = () => {
  try {
    const cachedRounds = sessionStorage.getItem(
      "safety_observation_rounds_data",
    );
    const cachedPlants = sessionStorage.getItem("plants_data");
    const cachedCategories = sessionStorage.getItem(
      "safety_observation_categories_data",
    );

    return {
      rounds: cachedRounds ? JSON.parse(cachedRounds) : [],
      plants: cachedPlants ? JSON.parse(cachedPlants) : [],
      categories: cachedCategories ? JSON.parse(cachedCategories) : [],
      hasAll: !!(cachedRounds && cachedPlants && cachedCategories),
    };
  } catch (e) {
    return { rounds: [], plants: [], categories: [], hasAll: false };
  }
};

const SafetyObservation = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("SafetyObservationRound.View");
  const canCreate = hasPermission("SafetyObservationRound.Create");
  const canEdit = hasPermission("SafetyObservationRound.Edit");
  const canDelete = hasPermission("SafetyObservationRound.Delete");

  console.log("canView", canView);
  console.log("canCreate", canCreate);
  console.log("canEdit", canEdit);
  console.log("canDelete", canDelete);

  const currentUser = authService.getUser();
  const currentUserRoleId = currentUser ? currentUser.roleId : null;

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedSafetyObservationData();
  const [rounds, setRounds] = useState(cachedData.rounds);
  const [plants, setPlants] = useState(cachedData.plants);
  const [categories, setCategories] = useState(cachedData.categories);

  const [loading, setLoading] = useState(!cachedData.hasAll);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    plantId: "",
    categoryId: "",
    observationType: "",
    startDate: "",
    endDate: "",
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });

  // Preview state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewRoundId, setPreviewRoundId] = useState(null);

  // Approval Modal State
  const [approvalModal, setApprovalModal] = useState({
    isOpen: false,
    roundId: null,
    status: "",
    remarks: "",
  });

  const handlePreviewClick = (roundId) => {
    setPreviewRoundId(roundId);
    setIsPreviewOpen(true);
  };

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedSafetyObservationData();
      if (refreshedCachedData.hasAll) {
        setRounds(refreshedCachedData.rounds);
        setPlants(refreshedCachedData.plants);
        setCategories(refreshedCachedData.categories);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setError("Please login to manage Safety Observation rounds");
      setLoading(false);
      return;
    }

    // Check if we have cached data - if yes, show page immediately
    const hasCachedData =
      sessionStorage.getItem("safety_observation_rounds_data") &&
      sessionStorage.getItem("plants_data") &&
      sessionStorage.getItem("safety_observation_categories_data");

    if (hasCachedData) {
      setLoading(false);
      if (canView) {
        loadInitialData();
      }
    }

    if (!canView) {
      setError(
        "You do not have permission to view Safety Observation rounds",
      );
      setLoading(false);
      return;
    }

    if (canView && !hasCachedData) {
      loadInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canView]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== "") {
        fetchRoundsWithFilters(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadInitialData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem(
        "safety_observation_rounds_data",
      );
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");

      const filterParams = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        sortBy: "createdAt",
        sortOrder: "desc",
        searchTerm: searchTerm,
      };

      const [response, plantsData, categoriesData] = await Promise.all([
        safetyObservationService.getSafetyObservationRounds(filterParams),
        plantService.getAllPlants(),
        safetyObservationService.getCategories(),
      ]);

      const roundsData = response?.data || response || [];
      if (response && response.data) {
        setRounds(response.data || []);
        setPagination((prev) => ({
          ...prev,
          totalCount: response.pagination?.totalCount || 0,
          totalPages: response.pagination?.totalPages || 0,
        }));
      } else {
        // Fallback for old API response format
        setRounds(response || []);
      }
      setPlants(plantsData || []);
      setCategories(categoriesData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      // Cache the data
      sessionStorage.setItem(
        "safety_observation_rounds_data",
        JSON.stringify(roundsData),
      );
      sessionStorage.setItem("plants_data", JSON.stringify(plantsData || []));
      sessionStorage.setItem(
        "safety_observation_categories_data",
        JSON.stringify(categoriesData || []),
      );
    } catch (err) {
      console.error("Error loading Safety Observation rounds:", err);
      setError("Failed to load Safety Observation rounds. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoundsWithFilters = async (page = pagination.currentPage) => {
    try {
      setRefreshing(true);
      setError("");

      const filterParams = {
        page: page,
        pageSize: pagination.pageSize,
        sortBy: "createdAt",
        sortOrder: "desc",
      };
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;
      if (filters.observationType)
        filterParams.observationType = filters.observationType;
      if (filters.categoryId)
        filterParams.categoryId = parseInt(filters.categoryId, 10);
      if (filters.plantId) filterParams.plantId = parseInt(filters.plantId, 10);
      if (searchTerm) filterParams.searchTerm = searchTerm;

      const response =
        await safetyObservationService.getSafetyObservationRounds(filterParams);
      if (response && response.data) {
        setRounds(response.data || []);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalCount: response.pagination?.totalCount || 0,
          totalPages: response.pagination?.totalPages || 0,
        }));
      } else {
        // Fallback for old API response format
        setRounds(response || []);
      }
    } catch (err) {
      console.error("Error fetching Safety Observation rounds:", err);
      setError("Failed to fetch Safety Observation rounds. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchRoundsWithFilters(1);
    setShowFilterDrawer(false);
  };

  const clearFilters = () => {
    setFilters({
      plantId: "",
      categoryId: "",
      observationType: "",
      startDate: "",
      endDate: "",
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchRoundsWithFilters(1);
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchRoundsWithFilters(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPagination((prev) => ({ ...prev, pageSize, currentPage: 1 }));
    fetchRoundsWithFilters(1);
  };

  const getPlantName = (plantId) => {
    if (!plantId) return "-";
    const p = plants.find((x) => x.plantId === plantId);
    return p ? p.plantName : `Plant ${plantId}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return d.toLocaleDateString();
  };

  const filteredRounds = useMemo(() => {
    let filtered = rounds;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (round) =>
          round.observationNumber?.toLowerCase().includes(term) ||
          round.area?.toLowerCase().includes(term),
      );
    }

    return filtered;
  }, [rounds, searchTerm]);

  const handleDelete = async (round) => {
    if (!canDelete) {
      setError(
        "You do not have permission to delete Safety Observation rounds",
      );
      return;
    }
    try {
      await safetyObservationService.deleteSafetyObservationRound(
        round.roundId,
      );
      toast.success("Observation deleted successfully");
      setDeleteConfirm(null);
      loadInitialData();
    } catch (err) {
      console.error("Error deleting Safety Observation round:", err);
      toast.error("Failed to delete Safety Observation round. Please try again.");
    }
  };

  const handleApproveReject = async () => {
    if (!approvalModal.remarks.trim()) {
      toast.error("Please provide remarks.");
      return;
    }

    try {
      await safetyObservationService.approveSafetyObservationRound(approvalModal.roundId, {
        approvalStatus: approvalModal.status,
        remarks: approvalModal.remarks,
      });

      const successMessage = `Safety Observation successfully ${approvalModal.status.toLowerCase()}`;
      toast.success(successMessage);

      setApprovalModal({
        isOpen: false,
        roundId: null,
        status: "",
        remarks: "",
      });
      fetchRoundsWithFilters();
    } catch (error) {
      console.error("Error approving observation:", error);
      toast.error(`Failed to ${approvalModal.status.toLowerCase()} observation`);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <PageLoading message="Loading observation rounds..." />
      </div>
    );
  }

  if (error && !canView) {
    return (
      <div className="page-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <HiOutlineShieldCheck className="title-icon" />
            Safety Observations
          </h1>
          {canCreate && (
            <button
              className="btn btn-primary"
              onClick={() => navigate("/home/safety-observation-entry")}
            >
              <HiOutlinePlus className="btn-icon" />
              New Observation
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <HiOutlineXMark className="alert-icon" />
          {error}
        </div>
      )}

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by observation number, round name or area..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button
          className="btn btn-secondary filter-btn"
          onClick={() => setShowFilterDrawer(true)}
        >
          <HiOutlineAdjustmentsHorizontal className="btn-icon" />
          Filters
        </button>
      </div>

      {showFilterDrawer && (
        <div
          className="filter-drawer-overlay"
          onClick={() => setShowFilterDrawer(false)}
        >
          <div className="filter-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="filter-drawer-header">
              <h3>Filters</h3>
              <button
                className="icon-btn"
                onClick={() => setShowFilterDrawer(false)}
              >
                <HiOutlineXMark />
              </button>
            </div>
            <div className="filter-drawer-content">
              <div className="form-group">
                <label>Plant</label>
                <select
                  name="plantId"
                  value={filters.plantId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Plants</option>
                  {plants.map((p) => (
                    <option key={p.plantId} value={p.plantId}>
                      {p.plantName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Observation Type</label>
                <select
                  name="observationType"
                  value={filters.observationType}
                  onChange={handleFilterChange}
                >
                  <option value="">All Types</option>
                  <option value="Safe Act">Safe Act</option>
                  <option value="Safe Condition">Safe Condition</option>
                  <option value="Unsafe Act">Unsafe Act</option>
                  <option value="Unsafe Condition">Unsafe Condition</option>
                </select>
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  name="categoryId"
                  value={filters.categoryId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.categoryId} value={c.categoryId}>
                      {c.categoryName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
            <div className="filter-drawer-footer">
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Observation #</th>
              <th>Date</th>
              <th>Plant</th>
              <th>Area</th>
              <th>Duration (min)</th>
              <th>Safe</th>
              <th>Unsafe</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRounds.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-state">
                  No Safety Observation rounds found
                </td>
              </tr>
            ) : (
              filteredRounds.map((round) => (
                <tr key={round.roundId}>
                  <td>
                    <button
                      className="obs-number-link"
                      onClick={() => handlePreviewClick(round.roundId)}
                    >
                      <HiOutlineShieldCheck className="obs-icon" />
                      {round.observationNumber}
                    </button>
                  </td>
                  <td>{formatDate(round.observationDate)}</td>
                  <td>{getPlantName(round.plantId)}</td>
                  <td>{round.area || "-"}</td>
                  <td>{round.observationDuration || "-"}</td>
                  <td>{round.safeCount ?? 0}</td>
                  <td>{round.unsafeCount ?? 0}</td>
                  <td>
                    <span
                      className={`status-badge ${(round.statusName || "Active").toLowerCase()}`}
                    >
                      {round.statusName || "Active"}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {round.approvalStatus === "Pending" &&
                        round.approverRoleId === currentUserRoleId && (
                          <div
                            className="approval-actions"
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "center",
                            }}
                          >
                            <button
                              className="btn-icon check"
                              title="Approve"
                              onClick={(e) => {
                                e.stopPropagation();
                                setApprovalModal({
                                  isOpen: true,
                                  roundId: round.roundId,
                                  status: "Approved",
                                  remarks: "",
                                });
                              }}
                              style={{
                                background: SEMANTIC_SUCCESS_MID,
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              className="btn-icon cross"
                              title="Reject"
                              onClick={(e) => {
                                e.stopPropagation();
                                setApprovalModal({
                                  isOpen: true,
                                  roundId: round.roundId,
                                  status: "Rejected",
                                  remarks: "",
                                });
                              }}
                              style={{
                                background: "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                cursor: "pointer",
                                fontSize: "12px",
                              }}
                            >
                              ✕ Reject
                            </button>
                          </div>
                      )}
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() =>
                            navigate(
                              `/home/safety-observation-entry/${round.roundId}`,
                            )
                          }
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(round)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete Safety Observation round{" "}
            <strong>{deleteConfirm?.observationNumber}</strong>? This action
            cannot be undone.
          </p>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={approvalModal.isOpen}
        onClose={() =>
          setApprovalModal({
            isOpen: false,
            roundId: null,
            status: "",
            remarks: "",
          })
        }
        title={`${approvalModal.status === "Approved" ? "Approve" : "Reject"} Observation`}
        customClass="small"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() =>
                setApprovalModal({
                  isOpen: false,
                  roundId: null,
                  status: "",
                  remarks: "",
                })
              }
            >
              Cancel
            </button>
            <button
              className={`btn ${
                approvalModal.status === "Approved"
                  ? "btn-primary"
                  : "btn-danger"
              }`}
              onClick={handleApproveReject}
            >
              {approvalModal.status === "Approved" ? "Approve" : "Reject"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="form-group mb-4">
            <label className="required mb-2" style={{ display: "block" }}>
              Remarks
            </label>
            <textarea
              value={approvalModal.remarks}
              onChange={(e) =>
                setApprovalModal({
                  ...approvalModal,
                  remarks: e.target.value,
                })
              }
              placeholder={`Enter reason for ${approvalModal.status.toLowerCase()}...`}
              required
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>
        </div>
      </SharedModal>

      {/* Observation Preview Modal */}
      {isPreviewOpen && (
        <ObservationPreview
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          roundId={previewRoundId}
        />
      )}
    </div>
  );
};

export default SafetyObservation;
